/**
 * Archivos Adjuntos API Service
 * CRUD operations for patient file attachments
 */

import { api } from "@/lib/api";

const ARCHIVOS_URL = "/archivos-adjuntos/";

// =============================================================================
// TYPES
// =============================================================================

export type TipoArchivo = "FOTO" | "PDF" | "DOCUMENTO" | "RESULTADO_EXTERNO" | "RECETA" | "OTRO";

export interface ArchivoAdjunto {
  id: number;
  paciente: number;
  paciente_nombre: string;
  tipo: TipoArchivo;
  tipo_display: string;
  nombre_archivo: string;
  archivo: string;
  descripcion: string;
  fecha_subida: string;
  tamaño_bytes: number;
  tamaño_legible: string;
  extension: string;
  es_imagen: boolean;
  es_pdf: boolean;
  url: string | null;
  is_active: boolean;
  created_at: string;
}

export interface ArchivoAdjuntoListItem {
  id: number;
  tipo: TipoArchivo;
  tipo_display: string;
  nombre_archivo: string;
  fecha_subida: string;
  tamaño_legible: string;
  es_imagen: boolean;
  es_pdf: boolean;
  url: string | null;
}

export interface ArchivoAdjuntoCreateData {
  paciente: number;
  tipo: TipoArchivo;
  nombre_archivo: string;
  archivo: File;
  descripcion?: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ArchivoAdjuntoFilters {
  paciente?: number;
  tipo?: TipoArchivo;
  is_active?: boolean;
  page?: number;
  page_size?: number;
  ordering?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

export const TIPOS_ARCHIVO: { value: TipoArchivo; label: string }[] = [
  { value: "FOTO", label: "Fotografía" },
  { value: "PDF", label: "Documento PDF" },
  { value: "DOCUMENTO", label: "Documento escaneado" },
  { value: "RESULTADO_EXTERNO", label: "Resultado de laboratorio externo" },
  { value: "RECETA", label: "Receta médica" },
  { value: "OTRO", label: "Otro" },
];

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Get paginated list of archivos adjuntos
 */
export async function getArchivosAdjuntos(
  params: ArchivoAdjuntoFilters = {}
): Promise<PaginatedResponse<ArchivoAdjuntoListItem>> {
  const searchParams = new URLSearchParams();

  if (params.paciente) searchParams.append("paciente", String(params.paciente));
  if (params.tipo) searchParams.append("tipo", params.tipo);
  if (params.is_active !== undefined) searchParams.append("is_active", String(params.is_active));
  if (params.page) searchParams.append("page", String(params.page));
  if (params.page_size) searchParams.append("page_size", String(params.page_size));
  if (params.ordering) searchParams.append("ordering", params.ordering);

  const url = searchParams.toString() ? `${ARCHIVOS_URL}?${searchParams}` : ARCHIVOS_URL;
  const response = await api.get<PaginatedResponse<ArchivoAdjuntoListItem>>(url);
  return response.data;
}

/**
 * Get a single archivo adjunto by ID
 */
export async function getArchivoAdjunto(id: number): Promise<ArchivoAdjunto> {
  const response = await api.get<ArchivoAdjunto>(`${ARCHIVOS_URL}${id}/`);
  return response.data;
}

/**
 * Create a new archivo adjunto (single file)
 */
export async function createArchivoAdjunto(
  data: ArchivoAdjuntoCreateData
): Promise<ArchivoAdjunto> {
  const formData = new FormData();
  formData.append("paciente", String(data.paciente));
  formData.append("tipo", data.tipo);
  formData.append("nombre_archivo", data.nombre_archivo);
  formData.append("archivo", data.archivo);
  if (data.descripcion) {
    formData.append("descripcion", data.descripcion);
  }

  const response = await api.post<ArchivoAdjunto>(ARCHIVOS_URL, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
}

/**
 * Create multiple archivos adjuntos (batch upload)
 */
export async function createArchivosAdjuntosBatch(
  pacienteId: number,
  tipo: TipoArchivo,
  files: File[],
  descripcion?: string
): Promise<ArchivoAdjunto[]> {
  const results: ArchivoAdjunto[] = [];

  for (const file of files) {
    const archivo = await createArchivoAdjunto({
      paciente: pacienteId,
      tipo,
      nombre_archivo: file.name,
      archivo: file,
      descripcion,
    });
    results.push(archivo);
  }

  return results;
}

/**
 * Hard delete an archivo adjunto (permanent removal)
 */
export async function deleteArchivoAdjunto(id: number): Promise<void> {
  await api.delete(`${ARCHIVOS_URL}${id}/`);
}

/**
 * Deactivate an archivo adjunto (soft delete)
 */
export async function deactivateArchivoAdjunto(id: number): Promise<ArchivoAdjunto> {
  const response = await api.post<ArchivoAdjunto>(`${ARCHIVOS_URL}${id}/deactivate/`);
  return response.data;
}

/**
 * Activate an archivo adjunto
 */
export async function activateArchivoAdjunto(id: number): Promise<ArchivoAdjunto> {
  const response = await api.post<ArchivoAdjunto>(`${ARCHIVOS_URL}${id}/activate/`);
  return response.data;
}
